<!--
  Created 05.01.2023
  Originated from Tailwind UI component: Lists, Tables, WithCondensedContent
  https://tailwindui.com/components/application-ui/lists/tables#component-fa5d8b35366e1921290491bd08878e0b
-->
<template>
  <div>
    <!-- Header -->
    <div
      v-if="displayHeader"
      class="relative flex flex-row"
      :class="hasOnlyButtonInHeader ? 'justify-end' : 'justify-between'">
      <!-- left-hand side: Heading -->
      <div
        class="flex flex-col"
        :class="headerClass">
        <!-- Heading and ingress -->
        <div
          v-if="(heading || ingress)"
          class="sm:flex-auto">
          <h2
            v-if="heading"
            :class="headingClass">
            {{ heading }}
          </h2>
          <p
            v-if="ingress"
            :class="ingressClass">
            {{ ingress }}
          </p>
        </div>
        <slot
          v-else
          name="header" />
        <!-- Search -->
        <Input
          v-if="hasSearch"
          v-model="search"
          type="text"
          class="mt-4 w-96"
          :placeholder="searchPlaceholder"
          icon="search" />
      </div>
      <!-- right-hand side: Button -->
      <div
        v-if="button"
        class="self-end sm:ml-16 sm:flex-none">
        <Link
          :href="button.link">
          <button
            type="button"
            class="inline-flex items-center justify-center gap-1 rounded-md border
              border-transparent px-4 py-2 text-sm font-medium hover:bg-indigo-700
              focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto"
            :class="button.isTransparent
              ? 'bg-transparent text-indigo-600 underline shadow-none hover:text-white hover:no-underline absolute right-0 bottom-0'
              : 'bg-indigo-600 text-white shadow-sm'
            ">
            <Component
              :is="button.icon"
              v-if="button.icon"
              class="size-5" />
            <span :class="{'-mt-0.5':button.icon}">{{ button.text }}</span>
          </button>
        </Link>
      </div>
      <slot
        v-else
        name="button" />
    </div>

    <!-- Content, Table -->
    <div class="mt-6 flex flex-col">
      <div class="-mx-4 -my-2 overflow-x-auto">
        <div class="inline-block min-w-full px-4 py-2 align-middle">
          <div class="overflow-hidden border-x border-gray-200 shadow ring-1 ring-black/5 md:rounded-lg">
            <table class="min-w-full divide-y divide-gray-300">
              <thead class="bg-indigo-50">
                <slot name="tableHeaders">
                  <tr>
                    <th
                      v-for="(tableHeading, headingIndex) in tableHeaders"
                      :key="'heading'+headingIndex"
                      scope="col"
                      class="cursor-pointer whitespace-nowrap px-6 py-3 text-left"
                      :class="[tableBold ? 'text-sm font-semibold text-gray-900' : 'text-xs font-medium uppercase tracking-wider text-gray-500']"
                      :title="'Click to sort by ' + tableHeading.toLowerCase()"
                      @click="sortTable(tableHeading)">
                      <div class="flex items-center space-x-2">
                        <span>{{ tableHeading }}</span>
                        <SortUpDownIcon
                          v-if="sortKey === tableHeading"
                          classes="text-indigo-500 pt-1"
                          :sort-order="sortOrder" />
                      </div>
                    </th>
                    <th
                      v-if="hasCellLink"
                      scope="col"
                      class="relative whitespace-nowrap py-3.5 pl-3 pr-4 sm:pr-6">
                      <span class="sr-only">Action</span>
                    </th>
                  </tr>
                </slot>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <template v-if="currentPageItems.length === 0">
                  <tr>
                    <td
                      :colspan="tableHeaders.length + (hasCellLink ? 1 : 0)"
                      class="whitespace-nowrap py-2 pl-4 pr-3 text-sm  sm:pl-6"
                      :class="[tableBold ? 'text-gray-600' : 'text-gray-500']">
                      {{ tablePlaceholderText }}
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <tr
                    v-for="(row, rowIndex) in currentPageItems"
                    :key="'row'+rowIndex"
                    class="hover:bg-gray-100">
                    <td
                      v-for="(cell, cellIndex) in row.filter((cell, cellIndex) => cellIndex!==tableHeaders.length)"
                      :key="'cell'+cellIndex"
                      class="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-gray-500 sm:pl-6"
                      :class="[tableBold ? 'text-gray-600' : 'text-gray-500']">
                      <template v-if="typeof cell === 'boolean'">
                        <span
                          class="inline-flex rounded-full px-2 text-xs font-semibold leading-5"
                          :class="(cell === true ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800')">
                          {{ (cell === true ? 'Yes' : 'No') }}
                        </span>
                      </template>
                      <template v-else-if="Array.isArray(cell)">
                        <ul class="list-none">
                          <li
                            v-for="(item, itemIndex) in cell"
                            :key="'item'+itemIndex"
                            class="text-xs italic first-of-type:text-sm first-of-type:not-italic"
                            :class="[tableBold ? 'text-gray-400 first-of-type:text-gray-600' : 'text-gray-400 first-of-type:text-gray-500']">
                            {{ item }}
                          </li>
                        </ul>
                      </template>
                      <template v-else-if="typeof cell === 'object'">
                        <StatusBadge
                          v-if="cell.type === 'status-badge'"
                          :class="cell.class ?? 'basicLarge'"
                          :status="cell.value" />
                      </template>
                      <template v-else>
                        {{ cell }}
                      </template>
                    </td>
                    <td
                      v-if="hasCellLink"
                      class="relative w-0 whitespace-nowrap py-2 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <Link
                        :href="row[row.length - 1]">
                        <a class="text-indigo-600 hover:text-indigo-900">{{ cellLinkText ?? row[row.length - 1] }}<span class="sr-only">, {{ row[rowIndex] }}</span></a>
                      </Link>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer, Paginator -->
    <div
      v-if="hasPaginator"
      class="mx-6 my-2 block text-right text-sm text-gray-400">
      <span v-if="totalPages > 1">
        {{ currentPage }} of {{ Math.ceil(tableRowsData.length / itemsPerPage) }} pages showing {{ currentPageItems.length }} of {{ tableRowsData.length }} results
      </span>
      <span v-else>
        {{ currentPage }} page showing {{ tableRowsData.length }} results
      </span>
    </div>
    <Paginator
      v-if="hasPaginator && totalPages > 1"
      class="my-8"
      :total-items="tableRowsData.length"
      :items-per-page="itemsPerPage"
      @page-changed="(page) => currentPage = page" />
  </div>
</template>

<script setup>
import {
  computed, ref, useSlots,
} from 'vue';
import { Link } from '@inertiajs/vue3';
import Input from '@/Jetstream/Input.vue';
import Paginator from '@/Components/Paginator.vue';
import StatusBadge from '@/ComponentsDeprecated/StatusBadge.vue';
import SortUpDownIcon from '@/Components/SortUpDownIcon.vue';
import { normalizeString } from '@/utils';

const props = defineProps({
  tableHeaders: {
    type: Array,
    required: true,
    default: () => [
      'Client', 'Project', 'Title', 'Status', 'IsBoolean',
    ],
  },
  sortKey: {
    type: String,
    default: '',
    required: false,
  },
  sortOrder: {
    type: String,
    default: 'asc',
    required: false,
  },
  tableBold: {
    type: Boolean,
    default: false,
    required: false,
  },
  tableRows: {
    type: Array,
    required: true,
    default: () => [
      [
        'DNB ASA', 'Kickoff 2023', 'Tur til Maldivene', {
          type: 'status', value: 'accepted',
        }, true,
      ],
      [
        'Telenor AS', ['Høstseminar', 'Sommerseminar'], 'Påfyll i Praha', {
          type: 'status', value: 'accepted',
        }, false,
      ],
    ],
  },
  tablePlaceholderText: {
    type: String,
    default: 'No items found.',
    required: false,
  },
  hasCellLink: {
    type: Boolean,
    default: false,
  },
  cellLinkText: {
    type: String,
    default: 'View item',
    required: false,
  },
  hasSearch: {
    type: Boolean,
    default: false,
    required: false,
  },
  searchPlaceholder: {
    type: String,
    default: 'Search for an item',
    required: false,
  },
  hasPaginator: {
    type: Boolean,
    default: false,
    required: false,
  },
  itemsPerPage: {
    type: Number,
    default: 18,
    required: false,
  },
  headerClass: {
    type: String,
    default: '',
    required: false,
  },
  heading: {
    type: String,
    default: '',
    required: false,
  },
  headingClass: {
    type: String,
    default: 'text-lg font-medium text-gray-800',
    required: false,
  },
  ingress: {
    type: String,
    default: '',
    required: false,
  },
  ingressClass: {
    type: String,
    default: 'mt-2 text-sm text-gray-700',
    required: false,
  },
  /**
   * Button above the table, right-hand side.
   * @type {object|boolean} - Button Object
   * @example { text: 'Create new', link: route('dashboard'), 'icon': PlusIcon, 'isTransparent': true }
   */
  button: {
    type: [Object, Boolean],
    default: () => {},
    required: false,
  },
});

const slots = useSlots();

const displayHeader = computed(() => props.heading || props.ingress || props.hasSearch || props.button || slots.header || slots.button);
const hasOnlyButtonInHeader = computed(() => !props.heading && !props.ingress && !props.hasSearch && !slots.header && (props.button || slots.button));

/**
 * Sorted table rows constants
 */
const sortKey = ref(props.sortKey !== '' ? props.sortKey : props.tableHeaders[0]);
const sortOrder = ref(props.sortOrder !== '' ? props.sortOrder : 'asc');

const headerToIndexMap = computed(() => {
  const map = {};
  props.tableHeaders.forEach((header, index) => {
    map[header] = index;
  });

  return map;
});

/**
 * Convert date string to Date object
 * @param {string} value - date string in format dd.mm.yyyy
 * @returns {Date|null} - Date object or null if invalid
 */
const convertToDate = (value) => {
  if (typeof value === 'string' && /^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
    const [day, month, year] = value.split('.').map(Number);

    return new Date(year, month - 1, day);
  }

  return null;
};

const extractLeadingNumber = (value) => {
  if (typeof value === 'string') {
    const match = value.match(/^\s*(\d+)/);

    return match ? Number(match[1]) : null;
  }

  return null;
};

/**
 * Sort table rows by sort key and order.
 * Checks if the value is a date string and converts it to a Date object for comparison.
 * If the value is an array, it uses the first element for comparison.
 * Also handles numbers in strings by extracting the leading number.
 */
const sortedTableRows = computed(() => {
  const index = headerToIndexMap.value[sortKey.value];

  return [...props.tableRows].sort((a, b) => {
    let aValue;
    if (Array.isArray(a[index])) {
      aValue = a[index][0];
    } else if (typeof a[index] === 'object' && a[index] !== null) {
      aValue = a[index].value;
    } else {
      aValue = a[index];
    }

    let bValue;
    if (Array.isArray(b[index])) {
      bValue = b[index][0];
    } else if (typeof b[index] === 'object' && b[index] !== null) {
      bValue = b[index].value;
    } else {
      bValue = b[index];
    }

    // Convert to Date object if possible
    const aDate = convertToDate(aValue);
    const bDate = convertToDate(bValue);
    let result = 0;

    if (aDate && bDate) {
      result = aDate - bDate;
    } else {
      // Check for a leading number in the string
      const aNum = extractLeadingNumber(aValue);
      const bNum = extractLeadingNumber(bValue);
      if (aNum !== null && bNum !== null) {
        result = aNum - bNum;
      } else if (aValue < bValue) {
        result = -1;
      } else if (aValue > bValue) {
        result = 1;
      }
    }

    return sortOrder.value === 'asc' ? result : -result;
  });
});

/**
 * Filtration/Search constants
 */
const search = ref('');
const tableRowsData = computed(() => {
  if (search.value === '') {
    return sortedTableRows.value;
  }

  return sortedTableRows.value.filter(
    (row) => row.some(
      (cell) => normalizeString(cell).includes(normalizeString(search.value)),
    ),
  );
});

/**
 * Paginator constants
 */
const currentPage = ref(1);

const currentPageItems = computed(() => {
  const start = (currentPage.value - 1) * props.itemsPerPage;
  const end = start + props.itemsPerPage;

  return tableRowsData.value.slice(start, end);
});

const totalPages = Math.ceil(tableRowsData.value.length / props.itemsPerPage);

/**
 * Sort table by header
 * @param {string} header - table header
 */
function sortTable(header) {
  if (sortKey.value === header) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = header;
    sortOrder.value = 'asc';
  }
}

</script>
